export interface CalendarUISettings {
  minHour?: number;
  maxHour?: number;
  calendarStep?: number;
}

export const DEFAULT_CALENDAR_UI_SETTINGS: CalendarUISettings = {
  minHour: 0,
  maxHour: 23,
  calendarStep: 60,
};

const MINUTES_PER_DAY = 24 * 60;

// Stored preferences round-trip through the API, so a value can come back as a
// string or a null, and older settings forms allowed hour combinations the
// current one no longer offers.
const toInteger = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : undefined;
};

/**
 * Keeps the calendar's time range renderable. An empty range (minHour === maxHour)
 * leaves the time grid with fewer slot groups than a group has slots, and the time
 * gutter then reads past the end of the group list (BACKOFFICE-366); a step of 0
 * asks for an infinite number of groups. Anything invalid falls back to the defaults.
 */
export const sanitizeCalendarUISettings = (
  settings?: CalendarUISettings | null,
): CalendarUISettings => {
  const minHour = toInteger(settings?.minHour) ?? DEFAULT_CALENDAR_UI_SETTINGS.minHour!;
  const maxHour = toInteger(settings?.maxHour) ?? DEFAULT_CALENDAR_UI_SETTINGS.maxHour!;
  const calendarStep = toInteger(settings?.calendarStep) ?? DEFAULT_CALENDAR_UI_SETTINGS.calendarStep!;

  const hoursAreValid = minHour >= 0 && maxHour <= 23 && minHour < maxHour;
  const stepIsValid = calendarStep > 0 && calendarStep <= MINUTES_PER_DAY;

  return {
    minHour: hoursAreValid ? minHour : DEFAULT_CALENDAR_UI_SETTINGS.minHour,
    maxHour: hoursAreValid ? maxHour : DEFAULT_CALENDAR_UI_SETTINGS.maxHour,
    calendarStep: stepIsValid ? calendarStep : DEFAULT_CALENDAR_UI_SETTINGS.calendarStep,
  };
};
